/**
 * DeveloperSection — SDK code viewer with syntax highlighting.
 *
 * Shows a split layout: left copy with numbered bullets,
 * right side tabbed code viewer with ts/cli/json tabs and
 * inline syntax highlighting.
 */
import * as React from "react";
import { Icon } from "./LandingIcons";

// ─── Code snippets ──────────────────────────────────────────────────────

const SNIPPETS: Record<string, string> = {
  "ts.ts": `import { defineAgent, z } from "@omni/sdk";

export default defineAgent({
  name: "release-engineer",
  model: "claude-haiku-4-5",

  memory: { scope: "team:eng" },

  tools: ["git", "npm", "github", "slack"],

  input: z.object({
    version: z.string(),
    breaking: z.boolean().default(false),
  }),

  async run({ version, breaking }, ctx) {
    const log = await ctx.tools.git.logSince("v0.4.2");
    const notes = await ctx.llm.summarize(log, { breaking });
    await ctx.tools.npm.publish({ tag: version });
    return ctx.respond.text(\`Shipped \${version}\`);
  },
});`,
  "cli.sh": `# Spin up a new skill — zero config
$ omni new skill weather-pro
✓ scaffolded ./weather-pro
✓ installed @omni/sdk
✓ wired to local Omni at :4127

$ omni dev
▸ hot-reload across iPhone, Mac, web
▸ logs streaming to terminal
▸ stop with ctrl-c

# Ship to the marketplace
$ omni publish
✓ static check       (0 errors)
✓ security scan      (0 findings)
✓ submitted for review — live in <24h`,
  "mcp.json": `{
  "name": "company-docs",
  "version": "1.2.0",
  "transport": "stdio",
  "capabilities": {
    "resources": true,
    "tools": ["search", "summarize", "cite"],
    "prompts": ["onboarding", "policy_qa"]
  },
  "auth": {
    "type": "oidc",
    "issuer": "https://auth.acme.com"
  },
  "rate_limit": "60/min/user",
  "memory_writes": "scoped:team"
}`,
};

// ─── Syntax highlighters ────────────────────────────────────────────────

function colorTs(line: string): React.ReactNode {
  const tokens: React.ReactNode[] = [];
  const re =
    /(\/\/.*$|"[^"]*"|`[^`]*`|\b(?:import|from|export|default|async|await|return|const|let|var|new|true|false)\b|\b[A-Z][A-Za-z0-9]*\b|\b[a-zA-Z_][a-zA-Z0-9_]*(?=\()|[0-9]+|.)/g;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(line))) {
    const s = m[0];
    if (s.startsWith("//")) {
      tokens.push(<span className="c" key={key++}>{s}</span>);
      continue;
    }
    if (s.startsWith('"') || s.startsWith("`")) {
      tokens.push(<span className="s" key={key++}>{s}</span>);
      continue;
    }
    if (
      /^(import|from|export|default|async|await|return|const|let|var|new|true|false)$/.test(
        s,
      )
    ) {
      tokens.push(<span className="k" key={key++}>{s}</span>);
      continue;
    }
    if (/^[A-Z]/.test(s) && s.length > 1) {
      tokens.push(<span className="n" key={key++}>{s}</span>);
      continue;
    }
    if (/^[0-9]+$/.test(s)) {
      tokens.push(<span className="n" key={key++}>{s}</span>);
      continue;
    }
    tokens.push(<span className="f" key={key++}>{s}</span>);
  }
  return tokens;
}

function colorSh(line: string): React.ReactNode {
  if (line.startsWith("#"))
    return <span className="c">{line || " "}</span>;
  if (line.startsWith("$")) {
    return (
      <>
        <span className="k">$</span>
        <span className="f">{line.slice(1)}</span>
      </>
    );
  }
  if (line.startsWith("✓")) return <span className="s">{line}</span>;
  if (line.startsWith("▸")) return <span className="n">{line}</span>;
  return <span className="f">{line || " "}</span>;
}

function colorJson(line: string): React.ReactNode {
  const m = line.match(/^(\s*)("[^"]*"\s*:\s*)?(.*)$/);
  if (!m) return line;
  const [, indent, jsonKey, val] = m;
  return (
    <>
      <span>{indent}</span>
      {jsonKey && <span className="k">{jsonKey}</span>}
      {val && val.startsWith('"') ? (
        <span className="s">{val}</span>
      ) : (
        <span className="f">{val}</span>
      )}
    </>
  );
}

// ─── Component ──────────────────────────────────────────────────────────

export function DeveloperSection() {
  const tabs = Object.keys(SNIPPETS);
  const [tab, setTab] = React.useState(tabs[0]);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SNIPPETS[tab]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy code snippet: ", err);
    }
  };

  return (
    <section id="developers" className="section">
      <div className="container devsplit">
        <div className="devsplit__copy">
          <div className="eyebrow eyebrow--accent">For developers</div>
          <h2>Build for one user, ship to fifteen thousand.</h2>
          <p>
            A TypeScript SDK that doesn't fight you. Hot-reload across every
            Omni surface. A marketplace with a fair split. The infra is already
            there — you bring the idea.
          </p>
          <ul className="devsplit__bullets">
            <li>
              <span className="num">01</span>
              <span>Type-safe SDK, full intellisense, zero codegen.</span>
            </li>
            <li>
              <span className="num">02</span>
              <span>Remote IDE — edit in the browser, run on device.</span>
            </li>
            <li>
              <span className="num">03</span>
              <span>MCP-native — bring any existing server in one line.</span>
            </li>
            <li>
              <span className="num">04</span>
              <span>
                Memory primitives — scoped, audited, version-controlled.
              </span>
            </li>
            <li>
              <span className="num">05</span>
              <span>
                Tools as code. No DAG editors. No drag-and-drop YAML.
              </span>
            </li>
            <li>
              <span className="num">06</span>
              <span>
                80% revenue split. Net-30 payouts. No platform tax.
              </span>
            </li>
          </ul>
          <div style={{ display: "flex", gap: 10 }}>
            <a
              className="btn btn--primary"
              href="https://github.com/omanandswami2005/omni-the-real-jarvis#readme"
              target="_blank"
              rel="noreferrer"
            >
              Read the SDK docs <Icon name="arrow" size={14} />
            </a>
            <a
              className="btn btn--ghost"
              href="https://github.com/omanandswami2005/omni-the-real-jarvis"
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="github" size={14} /> Browse on GitHub
            </a>
          </div>
        </div>

        <div className="code">
          <div className="code__tabs">
            {tabs.map((t) => (
              <button
                key={t}
                className={`code__tab ${t === tab ? "active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
            <button
              onClick={handleCopy}
              className={`code__copy-btn ${copied ? "copied" : ""}`}
              title="Copy code snippet"
            >
              <Icon name={copied ? "check" : "copy"} size={13} />
              <span>{copied ? "Copied!" : "Copy"}</span>
            </button>
          </div>
          <pre className="code__body">
            {SNIPPETS[tab].split("\n").map((line, i) => (
              <span className="ln" key={i}>
                {tab === "ts.ts"
                  ? colorTs(line)
                  : tab === "cli.sh"
                    ? colorSh(line)
                    : colorJson(line)}
              </span>
            ))}
          </pre>
        </div>
      </div>
    </section>
  );
}
